import { Provider, Type } from '@angular/core';
import { TestBed, waitForAsync } from '@angular/core/testing';
import { By } from '@angular/platform-browser';
import { BrowserDynamicTestingModule } from '@angular/platform-browser-dynamic/testing';

import { setProps } from '../internals/query';
import * as customMatchers from '../matchers';
import { overrideComponentIfProviderOverridesSpecified, overrideModules, SpecOverrides } from '../Spec/create-factory';
import { addMatchers } from '../core';
import { isType } from '../types';
import { nodeByDirective } from '../internals/node-by-directive';

import { HostComponent } from './host-component';
import { initialSpecWithHostModule } from './initial-module';
import { getSpecHostDefaultOptions, SpecHostOptions } from './options';
import { SpecHost } from './spec-host';

/**
 * @publicApi
 */
export type SpecHostFactory<C, H> = <HP>(
  template: string,
  overrides?: SpecHostOverrides<C, H, HP>
) => SpecHost<C, H & (HostComponent extends H ? HP : unknown)>;

/**
 * @publicApi
 */
export type PresetSpecHostFactory<C, H> = <HP>(
  template?: string,
  overrides?: SpecHostOverrides<C, H, HP>
) => SpecHost<C, H & (HostComponent extends H ? HP : unknown)>;

/**
 * @publicApi
 */
export interface SpecHostOverrides<C, H, HP> extends SpecOverrides<C> {
  hostProps?: HostComponent extends H ? HP : Partial<H>;
}

/**
 * @publicApi
 */
export function createHostFactory<C, H = HostComponent>(
  options: SpecHostOptions<C, H> & { template: string }
): PresetSpecHostFactory<C, H>;
/**
 * @publicApi
 */
export function createHostFactory<C, H = HostComponent>(typeOrOptions: Type<C> | SpecHostOptions<C, H>): SpecHostFactory<C, H>;
export function createHostFactory<C, H = HostComponent>(typeOrOptions: Type<C> | SpecHostOptions<C, H>): SpecHostFactory<C, H> {
  const options = isType(typeOrOptions)
    ? getSpecHostDefaultOptions<C, H>({ component: typeOrOptions })
    : getSpecHostDefaultOptions(typeOrOptions);

  const moduleMetadata = initialSpecWithHostModule<C, H>(options);

  beforeEach(
    waitForAsync(() => {
      addMatchers(customMatchers);
      TestBed.configureTestingModule(moduleMetadata);

      overrideModules(options);

      overrideComponentIfProviderOverridesSpecified(options);
    })
  );

  return <HP>(template?: string, overrides?: SpecHostOverrides<C, H, HP>) => {
    const defaults: SpecHostOverrides<C, H, HP> = { props: {}, hostProps: {} as any, detectChanges: true, providers: [] };
    const { detectChanges, props, hostProps, providers } = { ...defaults, ...overrides };

    if (providers && providers.length) {
      providers.forEach((provider: Provider) => {
        TestBed.overrideProvider((provider as any).provide, provider as any);
      });
    }

    TestBed.overrideModule(BrowserDynamicTestingModule, {
      set: {
        entryComponents: moduleMetadata.entryComponents
      }
    }).overrideComponent(options.host, {
      set: { template: template || options.template }
    });

    const Spec = createSpecHost(options, props, hostProps);

    if (options.detectChanges && detectChanges) {
      Spec.detectChanges();
    }

    return Spec;
  };
}

function createSpecHost<C, H, HP>(
  options: Required<SpecHostOptions<C, H>>,
  props?: Partial<C>,
  hostProps?: HP
): SpecHost<C, H & HP> {
  const hostFixture = TestBed.createComponent(options.host);
  const debugElement = hostFixture.debugElement.query(By.directive(options.component)) || hostFixture.debugElement;
  const debugNode = hostFixture.debugElement.queryAllNodes(nodeByDirective(options.component))[0];

  if (!debugNode) {
    throw new Error(`Cannot find component/directive ${options.component} in host template 😔`);
  }

  const hostComponent = setProps(hostFixture.componentInstance, hostProps);
  const component = setProps(debugNode.injector.get(options.component), props);

  return new SpecHost(
    hostComponent,
    hostFixture.debugElement,
    hostFixture.nativeElement,
    hostFixture,
    debugElement,
    component,
    debugElement.nativeElement
  );
}
